/**
 * Hand off (README §5; §10 "Ownership, handoff, and close"). Gated on a
 * ticket as a UI courtesy (resolution #2) — with none, this renders
 * "TICKET REQUIRED" and no confirm button at all, only an "Attach a ticket
 * first" action. The real enforcement is `hand-off`'s gate in services/ops
 * (`handoff-needs-ticket`, packages/shared/src/gates.ts); this modal still
 * surfaces that refusal via `OpsError` if the layer ever disagrees with what
 * the UI showed (e.g. the ticket was unlinked between render and confirm),
 * rather than assuming its own gate is the only one that matters.
 *
 * Internal state (`handMode`: live-warm / scheduled) and the client-facing
 * cue (`handCue`, one of the four `ClientDisclosure` values) are two
 * separate store fields and two separate arguments on `hand-off` — idea #4:
 * a hand-off can be firmly scheduled internally while the client only hears
 * "within SLA," including the option to send nothing at all. The SLA-range
 * cue's preview text is built from the thread's own `slaTargetS` via
 * `slaLabelFromSeconds` — never a typed-in number.
 *
 * "Attach a ticket first" / "Change" both set `pendingHandoff` and open the
 * ticket modal; TicketModal reads that flag on success and reopens this
 * modal instead of just closing (resolution #3 — see TicketModal.tsx).
 */
import { useEffect, useState } from 'react'
import type { ClientDisclosure, ThreadSummary } from '@communicator/shared'
import { Button, Modal, Select } from '../ui'
import { ops, OpsError } from '../../lib/ops'
import { useStore } from '../../store'
import { GENERIC_OP_ERROR, HANDOFF_CUE_LABEL, HANDOFF_CUE_ORDER, handoffCueText } from './format'

const MODE_OPTIONS = [
  { value: '', label: 'Choose one' },
  { value: 'live-warm', label: 'Live warm' },
  { value: 'scheduled', label: 'Scheduled' },
]

export function HandoffModal() {
  const modal = useStore((s) => s.modal)
  const activeId = useStore((s) => s.activeId)
  const threads = useStore((s) => s.threads)
  const peers = useStore((s) => s.peers)
  const handTech = useStore((s) => s.handTech)
  const handMode = useStore((s) => s.handMode)
  const handCue = useStore((s) => s.handCue)
  const set = useStore((s) => s.set)
  const closeModal = useStore((s) => s.closeModal)
  const openModal = useStore((s) => s.openModal)
  const upsertThread = useStore((s) => s.upsertThread)

  const open = modal === 'handoff'
  const thread = threads.find((t) => t.id === activeId) ?? null

  const [exactTime, setExactTime] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) {
      setError(null)
      setBusy(false)
    }
  }, [open])

  if (!open) return null

  const hasTicket = !!thread?.ticket
  const techs = peers.filter((p) => p.role === 'tech')
  const cue = handCue as ClientDisclosure | null
  const canConfirm =
    hasTicket && !!handTech && !!handMode && !!cue && (cue !== 'exact-time' || exactTime.trim().length > 0)

  function goAttach() {
    set({ pendingHandoff: true })
    openModal('ticket')
  }

  async function handleConfirm() {
    if (!activeId || !handTech || !handMode || !cue) return
    setBusy(true)
    setError(null)
    try {
      const result = (await ops.call('hand-off', {
        threadId: activeId,
        techId: handTech,
        internalState: handMode,
        clientDisclosure: cue,
        at: cue === 'exact-time' ? exactTime : undefined,
        // The same sentence already previewed above — composed from the
        // thread's own data, never a hardcoded SLA phrase. `none` sends
        // nothing, matching the preview shown for that choice.
        clientMessage: cue === 'none' ? undefined : handoffCueText(cue, techName, thread?.slaTargetS ?? 0, exactTime),
      })) as { thread: ThreadSummary }
      upsertThread(result.thread)
      set({ handTech: null, handMode: null, handCue: null })
      setExactTime('')
      closeModal()
    } catch (e) {
      setError(e instanceof OpsError ? e.message : GENERIC_OP_ERROR)
    } finally {
      setBusy(false)
    }
  }

  const techName = techs.find((t) => t.id === handTech)?.name ?? 'the tech'

  return (
    <Modal
      open={open}
      title="Hand off"
      onClose={closeModal}
      footer={
        hasTicket ? (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button data-testid="handoff-modal-confirm-btn" disabled={!canConfirm || busy} onClick={handleConfirm}>
              Hand off
            </Button>
          </div>
        ) : undefined
      }
    >
      {error && (
        <p
          data-testid="handoff-modal-error"
          role="alert"
          className="mb-3 rounded-[8px] bg-bad-subtle px-3 py-2 text-[12px] text-bad"
        >
          {error}
        </p>
      )}

      {!hasTicket ? (
        <div data-testid="handoff-modal-ticket-required">
          <p className="mb-3 text-[13px] font-semibold uppercase tracking-wide text-warn">TICKET REQUIRED</p>
          <p className="mb-3 text-[13px] text-mid">
            A hand-off needs a linked ticket. Attach one and this comes right back here.
          </p>
          <Button data-testid="handoff-modal-attach-first-btn" onClick={goAttach}>
            Attach a ticket first
          </Button>
        </div>
      ) : (
        <div data-testid="handoff-modal-body">
          <div
            data-testid="handoff-modal-ticket-card"
            className="mb-3 flex items-center justify-between rounded-[8px] border border-subtle bg-card px-2.5 py-2"
          >
            <span className="text-[13px] text-hi">{thread!.ticket}</span>
            <Button variant="secondary" data-testid="handoff-modal-change-btn" onClick={goAttach}>
              Change
            </Button>
          </div>

          <p className="mb-1 text-[11px] font-semibold uppercase text-lo">Hand off to</p>
          <div data-testid="handoff-modal-techs" className="mb-3 flex flex-wrap gap-2">
            {techs.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`handoff-modal-tech-${p.id}`}
                onClick={() => set({ handTech: p.id })}
                className={[
                  'rounded-[999px] border px-2.5 py-1 text-[12px]',
                  handTech === p.id ? 'border-cc-blue bg-cc-blue-pale text-cc-blue-text' : 'border-subtle bg-card text-mid',
                ].join(' ')}
              >
                {p.name}
                {/* COVERING IS NOT AVAILABILITY. It is a declaration about
                    taking NEW work from outside — the dispatch pool. Handing a
                    thread to a colleague is internal, and a tech who has turned
                    covering off is still sitting there with the console open.
                    Labelling them "unavailable" told dispatch not to hand off
                    to someone perfectly able to take it.

                    `online` is the honest signal: a live /ops/stream
                    connection, i.e. their browser is actually open. Undefined
                    means an older ops build that does not report it — say
                    nothing rather than assert either way.

                    `active` splits that further, and it is the one that matters
                    here: connected is a socket, away is a person. A locked
                    laptop stays online for hours, and offering that tech a
                    hand-off is how something sits untouched all afternoon.
                    Offline outranks away — no connection is the stronger
                    fact. */}
                {p.online === false ? ' · offline' : p.active === false ? ' · away' : ''}
              </button>
            ))}
          </div>

          <p className="mb-1 text-[11px] font-semibold uppercase text-lo">Internal state</p>
          <Select
            className="mb-3"
            value={handMode ?? ''}
            onChange={(v) => set({ handMode: (v || null) as typeof handMode })}
            options={MODE_OPTIONS}
            aria-label="Internal hand-off state"
          />

          <p className="mb-1 text-[11px] font-semibold uppercase text-lo">Client hears</p>
          <div data-testid="handoff-modal-cues" className="mb-3 flex flex-col gap-1.5">
            {HANDOFF_CUE_ORDER.map((c) => (
              <label key={c} className="flex items-center gap-2 text-[13px] text-mid">
                <input
                  type="radio"
                  name="handoff-cue"
                  data-testid={`handoff-modal-cue-${c}`}
                  checked={cue === c}
                  onChange={() => set({ handCue: c })}
                />
                {HANDOFF_CUE_LABEL[c]}
                {cue === c && c !== 'none' && (
                  <span data-testid="handoff-modal-cue-preview" className="text-[12px] text-lo">
                    "{handoffCueText(c, techName, thread?.slaTargetS ?? 0, exactTime)}"
                  </span>
                )}
              </label>
            ))}
          </div>

          {cue === 'exact-time' && (
            <input
              data-testid="handoff-modal-exact-time"
              type="datetime-local"
              value={exactTime}
              onChange={(e) => setExactTime(e.target.value)}
              className="h-[34px] rounded-[8px] border border-subtle bg-card px-2.5 text-[13px] text-hi"
            />
          )}
        </div>
      )}
    </Modal>
  )
}
